import { createAsyncThunk } from "@reduxjs/toolkit";
import ListingCollectionApiService from "../apiServices/ListingCollectionApiService";
import ProductApiService from "../apiServices/ProductApiService";
import {
  CreateListingCollectionPayload,
  UpdateListingCollectionPayload,
} from "../../interfaces/ListingCollectionObject";

// Collections matching a name. Errors propagate so callers' .unwrap() can
// show the real message (mirrors collectionSearchByName in BusinessThunk).
export const listingCollectionSearchByName = createAsyncThunk(
  "listingCollection/searchByName",
  async (searchRq: { collectionName: string }) => {
    return await ListingCollectionApiService.searchListingCollectionsByName(
      searchRq.collectionName
    );
  }
);

// One collection + its members in a single call. Errors propagate (no
// rejectWithValue) so the error `code` (e.g. NOT_FOUND) survives
// miniSerializeError and the UI can tell "wrong id" from a transient failure.
export const fetchListingCollectionById = createAsyncThunk(
  "listingCollection/fetchById",
  async (rq: { collectionId: string; includeListingDetails?: boolean }) => {
    return await ListingCollectionApiService.getListingCollectionById(
      rq.collectionId,
      rq.includeListingDetails ?? true
    );
  }
);

// A curator's own collections, for the admin "My Listing Collections" screen.
export const fetchMyListingCollections = createAsyncThunk(
  "listingCollection/fetchMine",
  async (creatorId: string) => {
    return await ListingCollectionApiService.getMyListingCollections(creatorId);
  }
);

// create/update/delete deliberately let errors propagate (no rejectWithValue)
// so a thrown collectionError's `code` survives Redux Toolkit's
// miniSerializeError for slice/UI branching.
export const createListingCollection = createAsyncThunk(
  "listingCollection/create",
  async (payload: CreateListingCollectionPayload) => {
    return await ListingCollectionApiService.createListingCollection(payload);
  }
);

export const updateListingCollection = createAsyncThunk(
  "listingCollection/update",
  async (payload: UpdateListingCollectionPayload) => {
    return await ListingCollectionApiService.updateListingCollection(payload);
  }
);

export const deleteListingCollection = createAsyncThunk(
  "listingCollection/delete",
  async (collectionId: string) => {
    await ListingCollectionApiService.deleteListingCollection(collectionId);
    return collectionId;
  }
);

// Fetches all products for a given business. Result is transient UI staging
// state — not stored in any slice. Component receives it via .unwrap().
export const fetchProductsByBusinessId = createAsyncThunk(
  "listingCollection/fetchProductsByBusiness",
  async (businessId: string) => {
    return await ProductApiService.getProductsByBusinessId(businessId);
  }
);
